/**
 * This component is used to manage different types of documents and images 
 * based on their criticality, with an option to upload and assign criticality.
 * uploaded Files display and upload docs functionlaity is here 
 */

import React, { useState, useEffect, useRef } from "react";
import { Search, Upload, X, AlertCircle, ForwardIcon, FileText, Image, Download, FileIcon, FileMusic } from "lucide-react";
import "./Circular.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getData } from '../../../../Services/Services';
import { getThemeURL } from '../../../../Services/EndPoints';


const Circulars = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);
  const [categories, setCategories] = useState(["All", "High", "Medium", "Low"]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadPriority, setUploadPriority] = useState("Medium");
  const [errorMessage, setErrorMessage] = useState("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  // Sample documents to start with
  const initialDocuments = [
    {
      id: 1,
      title: "Employee Handbook",
      date: "05-01-2025",
      priority: "Medium",
      type: "pdf",
      docSrc: "/documents/employee_handbook.pdf",
      uploadedBy: "HR Department"
    },
    {
      id: 2,
      title: "IT Security Policy",
      date: "18-02-2025",
      priority: "High",
      type: "pdf",
      docSrc: "/documents/security_policy.docx",
      uploadedBy: "IT Department"
    },
    {
      id: 3,
      title: "Annual Report",
      date: "28-03-2025",
      priority: "High",
      type: "pdf",
      docSrc: "/documents/annual_report_2024.pdf",
      uploadedBy: "Finance Team"
    },
    {
      id: 4,
      title: "Marketing Strategy Q2",
      date: "12-04-2025",
      priority: "Low",
      type: "ppt",
      docSrc: "/documents/marketing_q2.pptx",
      uploadedBy: "Marketing Team"
    },
    {
      id: 5,
      title: "Remote Work Guidelines",
      date: "25-03-2025",
      priority: "Medium",
      type: "pdf",
      docSrc: "/documents/remote_guidelines.pdf",
      uploadedBy: "Admin Team"
    },
    {
      id: 6,
      title: "New Client Onboarding",
      date: "10-04-2025",
      priority: "High",
      type: "pdf",
      docSrc: "/documents/onboarding.pdf",
      uploadedBy: "Sales Team"
    },
    {
      id: 7,
      title: "Health & Safety Protocols",
      date: "02-05-2025",
      priority: "High",
      type: "pdf",
      docSrc: "/documents/health_safety.pdf",
      uploadedBy: "Operations Team"
    },
    {
      id: 8,
      title: "Customer Feedback Summary",
      date: "20-04-2025",
      priority: "Low",
      type: "xlsx",
      docSrc: "/documents/feedback_summary.xlsx",
      uploadedBy: "Customer Service"
    },
    {
      id: 9,
      title: "Team Building Activities",
      date: "15-03-2025",
      priority: "Low",
      type: "doc",
      docSrc: "/documents/team_building.docx",
      uploadedBy: "HR Department"
    },
    {
      id: 10,
      title: "Cybersecurity Awareness",
      date: "30-04-2025",
      priority: "Medium",
      type: "pdf",
      docSrc: "/documents/cyber_awareness.pdf",
      uploadedBy: "IT Department"
    }
  ];

  const getStaticDocumentPath = (type) => {
    const sampleFiles = {
      pdf: '/static/docs/sample.pdf',
      excel: '/static/docs/sample.xlsx',
      word: '/static/docs/sample.docx',
      powerpoint: '/static/docs/sample.pptx',
      image: '/static/docs/sample.jpg',
      video: '/static/docs/sample.mp4',
      audio: '/static/docs/sample.mp3',
      text: '/static/docs/sample.txt'
    };
    return sampleFiles[type.toLowerCase()] || null;
  };

  // Initialize uploaded documents with sample data on component mount
  useEffect(() => {
    setUploadedDocuments(initialDocuments);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Effect to handle audio playback
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(error => {
          console.error("Error playing audio:", error);
          setIsPlaying(false);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  // Effect to handle audio loading when current audio changes
  useEffect(() => {
    if (currentAudio && audioRef.current) {
      // Reset the audio player
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      setDuration(0);
      setIsPlaying(false);
    }
  }, [currentAudio]);

  // Effect to update audio time
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [currentAudio]);

  const handleAudioClick = (announcement) => {
    if (announcement.type === "audio") {
      setCurrentAudio(announcement);
      setShowAudioPlayer(true);
    }
  };

  const handleDocClick = (document) => {
    setCurrentDoc(document);
    setShowDocViewer(true);
  };

  const handleCardClick = (announcement) => {
    if (announcement.type === "audio") {
      handleAudioClick(announcement);
    } else if (announcement.type === "document") {
      handleDocClick(announcement);
    }
  };

  const closeAudioPlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    setShowAudioPlayer(false);
    setCurrentAudio(null);
  };

  const closeDocViewer = () => {
    setShowDocViewer(false);
    setCurrentDoc(null);
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setSelectedCategory(tab);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await getData(getThemeURL);
      if (response && Array.isArray(response) && response.length > 0) {
        setUserId(response[0].user_name); // Get `id` from the first user
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  // Function to handle audio end
  const handleAudioEnd = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  // Format time in MM:SS format
  const formatTime = (time) => {
    if (isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle progress bar click to seek
  const handleProgressBarClick = (e) => {
    if (!progressBarRef.current || !audioRef.current) return;

    const progressBar = progressBarRef.current;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressBarWidth = rect.width;

    // Calculate percentage
    const percentage = clickX / progressBarWidth;

    // Calculate new time
    const newTime = percentage * duration;

    // Set audio time
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);

    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "High":
        return "status-badge-high";
      case "Medium":
        return "status-badge-medium";
      case "Low":
        return "status-badge-low";
      default:
        return "status-badge-default";
    }
  };

  const handleUploadClick = () => {
    setShowUploadModal(true);
    setSelectedFiles([]);
    setUploadPriority("Medium");
    setErrorMessage("");
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setSelectedFiles([]);
    setErrorMessage("");
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    // Check if any file is larger than 5MB
    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);

    if (oversizedFiles.length > 0) {
      setErrorMessage("Some files exceed the 5MB limit and were not added.");
      // Filter out oversized files
      const validFiles = files.filter(file => file.size <= 5 * 1024 * 1024);
      setSelectedFiles(prev => [...prev, ...validFiles]);
    } else {
      setErrorMessage("");
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handlePriorityChange = (e) => {
    setUploadPriority(e.target.value);
  };

  const handleUploadSubmit = () => {
    if (selectedFiles.length === 0) {
      setErrorMessage("Please select at least one file to upload.");
      return;
    }

    const newDocuments = selectedFiles.map((file, index) => {
      // Get current date
      const today = new Date();
      // Format as DD-MM-YYYY
      const formattedDate = `${today.getDate().toString().padStart(2, '0')}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getFullYear()}`;

      return {
        id: Date.now() + index,
        title: file.name,
        date: formattedDate,
        playedAt: "All Branches",
        frequency: "One-time",
        priority: uploadPriority,
        type: "document",
        docSrc: URL.createObjectURL(file),
        file: file
      };
    });

    setUploadedDocuments(prev => [...newDocuments, ...prev]);
    closeUploadModal();
  };
  const toggleCategoryDropdown = () => {
    setShowCategoryDropdown(!showCategoryDropdown);
    if (showMonthDropdown) setShowMonthDropdown(false);
  };
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setShowCategoryDropdown(false);
  };

  // Calculate progress percentage for the audio progress bar
  const calculateProgress = () => {
    if (!duration) return 0;
    return (currentTime / duration) * 100;
  };

  // Filter documents based on selected category and search query
  const filteredDocuments = uploadedDocuments.filter(doc => {
    const matchesCategory = selectedCategory === "All" || doc.priority === selectedCategory;
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });




  // Play/Pause Icon Component
  const PlayPauseIcon = ({ isPlaying }) => (
    isPlaying ? (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="4" width="4" height="16" rx="1" fill="#2596be" />
        <rect x="14" y="4" width="4" height="16" rx="1" fill="#2596be" />
      </svg>
    ) : (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 4L19 12L6 20V4Z" fill="#2596be" />
      </svg>
    )
  );


  const getFileIcon = (type) => {
    switch (type) {
      case 'folder':
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 19C22 19.5304 21.7893 20.0391 21.4142 20.4142C21.0391 20.7893 20.5304 21 20 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H9L11 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V19Z" fill="#90CAF9" stroke="#42A5F5" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'pdf':
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z" fill="#FFCDD2" stroke="#E57373" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 3V8H19M11.5 13.5H9.5M12 17H9.5M14.5 10H9.5" stroke="#E57373" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'excel':
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z" fill="#C8E6C9" stroke="#81C784" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 3V8H19M9 13H15M9 17H15M9 9H10" stroke="#81C784" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'text':
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z" fill="#E1F5FE" stroke="#4FC3F7" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 3V8H19M9 13H15M9 17H15M9 9H10" stroke="#4FC3F7" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'powerpoint':
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z" fill="#FFECB3" stroke="#FFD54F" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 3V8H19M9 13H15M9 17H15M9 9H10" stroke="#FFD54F" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'word':
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z" fill="#BBDEFB" stroke="#64B5F6" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 3V8H19M9 13H15M9 17H15M9 9H10" stroke="#64B5F6" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'archive':
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z" fill="#D7CCC8" stroke="#A1887F" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 3V8H19M10 12H14M10 16H14" stroke="#A1887F" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'audio':
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z" fill="#E1BEE7" stroke="#BA68C8" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 3V8H19" stroke="#BA68C8" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12 11V17M10 13V15M14 13V15" stroke="#BA68C8" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'video':
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z" fill="#FFCCBC" stroke="#FF8A65" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 3V8H19" stroke="#FF8A65" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 13L15 13.5V16.5L10 17V13Z" stroke="#FF8A65" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'image':
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z" fill="#C8E6C9" stroke="#81C784" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 3V8H19" stroke="#81C784" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="10.5" cy="11.5" r="1.5" fill="#81C784" />
              <path d="M7 18L10 15L12 17L15 14L17 16" stroke="#81C784" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3H7C5.89543 3 5 3.89543 5 5V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V8L14 3Z" fill="#F5F5F5" stroke="#9E9E9E" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 3V8H19M9 13H15M9 17H15M9 9H10" stroke="#9E9E9E" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
    }
  };
  const getFileType = (extension) => {
    const documentTypes = ['pdf', 'docx', 'xlsx', 'pptx', 'ppt', 'txt'];
    const imageTypes = ['jpg', 'jpeg', 'png'];

    if (documentTypes.includes(extension)) return extension;
    if (imageTypes.includes(extension)) return extension === 'jpeg' ? 'jpg' : extension;
    return 'other';
  };



  const handleViewClick = (doc, e) => {
    e.stopPropagation();
    const filePath = getStaticDocumentPath(doc.type);
    setPreviewDocument({
      ...doc,
      previewUrl: doc.url || filePath
    });
    setShowPreviewModal(true);
  };

  const handleDownload = (url, fileName, fileType) => {
    //url ||
    const downloadUrl = getStaticDocumentPath(fileType);
    if (!downloadUrl) return;

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName || `sample.${fileType.toLowerCase()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteClick = (docId, e) => {
    e.stopPropagation();

    // Create custom alert box
    const alertBox = document.createElement('div');
    alertBox.style.position = 'fixed';
    alertBox.style.top = '50%';
    alertBox.style.left = '50%';
    alertBox.style.transform = 'translate(-50%, -50%)';
    alertBox.style.backgroundColor = 'white';
    alertBox.style.padding = '20px';
    alertBox.style.borderRadius = '8px';
    alertBox.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
    alertBox.style.zIndex = '1000';
    alertBox.style.textAlign = 'center';
    alertBox.style.maxWidth = '300px';
    alertBox.style.width = '100%';

    // Add title with delete icon (using Unicode symbol)
    const title = document.createElement('h3');
    title.innerHTML = '⚠️ Confirm Deletion';
    title.style.margin = '0 0 15px 0';
    title.style.color = '#1a7a73';
    title.style.fontSize = '18px';
    title.style.display = 'flex';
    title.style.alignItems = 'center';
    title.style.justifyContent = 'center';
    title.style.gap = '8px';

    const message = document.createElement('p');
    message.textContent = 'Are you sure you want to delete this document?';
    message.style.marginBottom = '20px';
    message.style.color = '#555';

    const buttonContainer = document.createElement('div');
    buttonContainer.style.display = 'flex';
    buttonContainer.style.justifyContent = 'center';
    buttonContainer.style.gap = '10px';

    const confirmButton = document.createElement('button');
    confirmButton.textContent = 'Delete';
    confirmButton.style.padding = '8px 16px';
    confirmButton.style.backgroundColor = '#1a7a73';
    confirmButton.style.color = 'white';
    confirmButton.style.border = 'none';
    confirmButton.style.borderRadius = '4px';
    confirmButton.style.cursor = 'pointer';
    confirmButton.style.fontWeight = 'bold';

    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.padding = '8px 16px';
    cancelButton.style.backgroundColor = '#f0f0f0';
    cancelButton.style.color = '#333';
    cancelButton.style.border = 'none';
    cancelButton.style.borderRadius = '4px';
    cancelButton.style.cursor = 'pointer';

    // Append elements
    buttonContainer.appendChild(cancelButton);
    buttonContainer.appendChild(confirmButton);
    alertBox.appendChild(title);
    alertBox.appendChild(message);
    alertBox.appendChild(buttonContainer);
    document.body.appendChild(alertBox);

    // Add overlay
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(0,0,0,0.5)';
    overlay.style.zIndex = '999';
    document.body.appendChild(overlay);

    // Handle cleanup
    const cleanup = () => {
      document.body.removeChild(alertBox);
      document.body.removeChild(overlay);
    };

    confirmButton.onclick = () => {
      cleanup();
      setUploadedDocuments(prevDocs => prevDocs.filter(doc => doc.id !== docId));
      toast.success("Document deleted successfully!", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    };

    cancelButton.onclick = cleanup;
    overlay.onclick = cleanup;
  };

  return (
    <div className="main-container">
      {/* Header Tabs */}
      <div className="header-tabs">

        <div className="header-button-container">
          <button
            onClick={handleUploadClick}
            className="primary-button btn-space"
          >
            Upload a Document
          </button>
        </div>
      </div>

      {/* Content area with gray background and rounded corners */}
      <div className="content-container">
        {/* Filter Tabs and Search */}
        <div className={`filter-container ${windowWidth < 768 ? 'filter-container-mobile' : ''}`}>
          <div className={`categories-tabs ${windowWidth < 768 ? 'categories-tabs-mobile' : ''}`}>
            {categories.map((category, index) => (
              <React.Fragment key={category}>
                <button
                  onClick={() => handleTabClick(category)}
                  className={`category-tab ${activeTab === category ? 'category-tab-active' : ''}`}
                >
                  {category}
                </button>
                {index < categories.length - 1 && (
                  <div className="category-divider" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Search Bar */}
          <div style={{
            width: windowWidth < 768 ? '200px' : '200px', // Reduced width values
            position: "relative"
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #ddd",
              borderRadius: "5px",
              padding: "0 10px",
              backgroundColor: "white",
              width: "100%", // Ensure the inner div takes full width of container
              boxSizing: "border-box", // Include padding in width calculation
              marginLeft:'10px'
            }}>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearch}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  padding: "3px 4px",
                  fontSize: "14px",
                  minWidth: "0" // Allow the input to shrink
                }}
              />
              <Search size={18} color="#666" />
            </div>
          </div>
          {/* Dropdowns */}
          {/* <div className={`dropdowns-container ${windowWidth < 768 ? 'dropdowns-container-mobile' : ''}`}>
            <div className={`dropdown-wrapper ${windowWidth < 768 ? 'dropdown-wrapper-mobile' : ''}`}>
              <button
                onClick={toggleCategoryDropdown}
                className={`dropdown-button ${windowWidth < 768 ? 'dropdown-button-mobile' : ''}`}
              >
                All Categories <span className="dropdown-arrow">▼</span>
              </button>
              {showCategoryDropdown && (
                <div className="dropdown-menu">
                  {categories.map((category) => (
                    <div
                      key={category}
                      onClick={() => handleCategorySelect(category)}
                      className={`dropdown-item ${selectedCategory === category ? 'dropdown-item-active' : ''}`}
                    >
                      {category}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div> */}
        </div>

        {/* Documents Grid */}
        <div
          className="documents-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "20px",
            marginTop: "-10px"
          }}
        >
          {filteredDocuments.length > 0 ? (
            filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                style={{
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                  backgroundColor: "#fff",
                  position: "relative",
                  cursor: "pointer",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  height: "130px", // Fixed height
                  display: "flex",
                  flexDirection: "column"
                }}
                onClick={() => handleCardClick(doc)}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 12px rgba(0, 0, 0, 0.1)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.05)";
                }}
              >
                {/* Card Content */}
                <div style={{ padding: "8px 16px 16px", flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "flex-start" }}>
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: "#2596be",
                        display: "flex",
                        alignItems: "center",
                        marginTop: "20px",
                        justifyContent: "center",
                        marginRight: "14px"
                      }}
                    >
                      {getFileIcon(doc.type)}
                    </div>

                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <div style={{
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "#333",
                        marginTop: "8px",
                        marginBottom: "4px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}>
                        {doc.title}
                      </div>

                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between"
                      }}>
                        <p style={{ fontSize: "12px", color: "#aaa", margin: "4px 0" }}>
                          {doc.date}
                        </p>

                        <div
                          className={`status-badge-circular ${getStatusBadgeClass(doc.priority)}`}
                          style={{
                            fontSize: "12px",
                            backgroundColor: doc.priority === "High" ? "#fff2f0" :
                              doc.priority === "Medium" ? "rgba(255, 161, 0, 0.1)" : "rgba(0, 191, 40, 0.1)",
                            borderRadius: "12px",
                            padding: "0px 10px",
                            marginTop: '25px',
                            fontWeight: 500,
                            color: doc.priority === "High" ? "#ff4d4f" :
                              doc.priority === "Medium" ? "#FFA100" : "#11B981",
                            display: "inline-block"
                          }}
                        >
                          {doc.priority}
                        </div>
                      </div>

                      <div style={{ fontSize: "12px", color: "#aaa", marginTop: "4px" }}>
                        {`Uploaded by: ${doc.uploadedBy}`}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Footer - Icons with click handlers */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  padding: '0 5px 16px',
                  marginTop: '-3px',
                }}>
                  {/* View icon */}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={(e) => handleViewClick(doc, e)}
                    style={{ cursor: 'pointer' }}
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#2596be" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="12" r="3" stroke="#2596be" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("../create-circular");
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      marginBottom: "5px",
                      cursor: 'pointer'
                    }}
                  >
                    <ForwardIcon
                      className="cursor-pointer"
                      style={{
                        width: "16px",
                        height: "16px",
                        color: "#2596be",
                        transition: "transform 0.2s",
                        ":hover": {
                          transform: "translateX(2px)"
                        }
                      }}
                    />
                  </span>

                  {/* Delete icon */}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    onClick={(e) => handleDeleteClick(doc.id, e)}
                    style={{ cursor: 'pointer' }}
                  >
                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 11v6M14 11v6" stroke="#ff4d4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>

                </div>
              </div>
            ))
          ) : (
            <div className="no-documents-message" style={{
              textAlign: "center",
              padding: "40px",
              color: "#888",
              gridColumn: "1 / -1"
            }}>
              No documents found.
            </div>
          )}
        </div>
      </div>

      {/* Audio Player Modal */}
      {showAudioPlayer && currentAudio && (
        <div className="modal-overlay">
          <div className="modal-content audio-player-modal">
            <div className="modal-header">
              <h6>{currentAudio.title}</h6>
              <button
                onClick={closeAudioPlayer}
                className="modal-close-button"
              >
                ✕
              </button>
            </div>

            {/* Hidden audio element - this is what actually plays the audio */}
            <audio
              ref={audioRef}
              src={currentAudio.audioSrc}
              onEnded={handleAudioEnd}
              preload="auto"
            />

            {/* Custom audio player UI */}
            <div className="audio-player-controls">
              <button
                onClick={togglePlay}
                className="play-pause-button"
              >
                <PlayPauseIcon isPlaying={isPlaying} />
              </button>

              {/* Progress bar */}
              <div
                ref={progressBarRef}
                onClick={handleProgressBarClick}
                className="progress-bar-container"
              >
                <div
                  className="progress-bar"
                  style={{ width: `${calculateProgress()}%` }}
                ></div>
              </div>

              {/* Time display */}
              <div className="time-display">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            <div className="audio-player-info">
              <p>Date: {currentAudio.date}</p>
              <p>Played at: {currentAudio.playedAt} &nbsp; | &nbsp; Frequency: {currentAudio.frequency}</p>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content upload-modal">
            <div className="modal-header">
              <h3 className="modal-title">Upload Documents</h3>
              <button
                onClick={closeUploadModal}
                className="modal-close-button"
              >
                <X size={24} />
              </button>
            </div>

            {/* File selection area */}
            <div className="upload-dropzone">
              <div className="upload-icon-container">
                <Upload size={48} className="upload-icon" />
              </div>
              <p className="upload-text">
                Drag & drop files here or click to browse
              </p>
              <p className="upload-hint">
                Supported formats: PDF, DOCX, XLSX, PPT, TXT, JPG, PNG
                <br />
                Maximum file size: 5MB
              </p>

              <input
                type="file"
                id="fileUpload"
                onChange={handleFileSelect}
                multiple
                className="file-input"
                accept=".pdf,.docx,.xlsx,.pptx,.ppt,.txt,.jpg,.jpeg,.png"
              />

              <button
                onClick={() => document.getElementById('fileUpload').click()}
                className="primary-button browse-button"
              >
                Browse Files
              </button>
            </div>

            {/* Selected files list */}
            {selectedFiles.length > 0 && (
              <div className="selected-files-container">
                <h4 className="selected-files-title">Selected Files ({selectedFiles.length})</h4>
                <div className="files-list">
                  {selectedFiles.map((file, index) => {
                    const fileExtension = file.name.split('.').pop().toLowerCase();
                    const fileType = getFileType(fileExtension);

                    return (
                      <div
                        key={index}
                        className="file-item"
                      >
                        <div className="file-icon">
                          {fileType === 'pdf' && <FileText size={20} className="file-icon-pdf" />}
                          {fileType === 'docx' && <FileText size={20} className="file-icon-doc" />}
                          {fileType === 'xlsx' && <FileText size={20} className="file-icon-xls" />}
                          {fileType === 'ppt' && <FileText size={20} className="file-icon-ppt" />}
                          {fileType === 'txt' && <FileText size={20} className="file-icon-txt" />}
                          {(fileType === 'jpg' || fileType === 'png') && <Image size={20} className="file-icon-img" />}
                        </div>
                        <div className="file-details">
                          <div className="file-name">{file.name}</div>
                          <div className="file-meta">
                            <span className="file-type">{fileExtension.toUpperCase()}</span>
                            <span className="file-size">
                              {(file.size / (1024 * 1024)).toFixed(2)} MB
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveFile(index)}
                          className="remove-file-button"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Error message */}
            {errorMessage && (
              <div className="error-message">
                <AlertCircle size={16} />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Priority selection */}
            <div className="priority-container">
              <label className="priority-label">
                Document Priority:
              </label>
              <div className="priority-options">
                {["High", "Medium", "Low"].map((priority) => (
                  <button
                    key={priority}
                    className={`priority-option ${uploadPriority === priority ? 'active' : ''}`}
                    onClick={() => handlePriorityChange({ target: { value: priority } })}
                  >
                    {priority}
                  </button>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="modal-actions">
              <button
                onClick={closeUploadModal}
                className="secondary-button"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadSubmit}
                className="primary-button"
                disabled={selectedFiles.length === 0}
              >
                Upload Documents
              </button>
            </div>
          </div>
        </div>
      )}

      {showPreviewModal && previewDocument && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowPreviewModal(false);
          }}
        >
          <div
            className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              width: "90%",
              maxWidth: "900px",
              maxHeight: "90vh",
              boxShadow: "0 4px 15px rgba(0, 0, 0, 0.15)",
              animation: "fadeIn 0.3s ease-out",
              border: "1px solid #e0e0e0",
              display: "flex",
              marginLeft: '60px',
              flexDirection: "column"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 20px",
              borderBottom: "1px solid #f0f0f0"
            }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#2596be",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "14px"
                  }}
                >
                  {getFileIcon(previewDocument.type)}
                </div>
                <div>
                  <h2 style={{ fontWeight: "600", fontSize: "18px", color: "#333", marginBottom: "4px" }}>
                    {previewDocument.name}
                  </h2>
                  <div style={{ display: "flex", alignItems: "center", fontSize: "12px", color: "#888" }}>
                    <span style={{ marginRight: "16px" }}>
                      {previewDocument.type} • {previewDocument.size}
                    </span>
                    <span>Uploaded by: {previewDocument.uploadedBy}</span>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center" }}>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    color: "#888",
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  &times;
                </button>
              </div>
            </div>

            {/* Document Preview Area */}
            <div style={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              padding: "0"
            }}>
              {/* Document Title */}
              <div style={{
                padding: "16px 20px",
                borderBottom: "1px solid #f0f0f0",
                backgroundColor: "#f9f9f9"
              }}>
                <h3 style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#333",
                  margin: 0
                }}>
                  {previewDocument.title || "Document Preview"}
                </h3>
              </div>

              {/* Preview content based on document type */}
              {previewDocument.type === "image" ? (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "20px",
                  height: "100%",
                  backgroundColor: "#f9f9f9"
                }}>
                  <img
                    src={previewDocument.url || getStaticDocumentPath('image') || `/api/placeholder/800/600`}
                    alt={previewDocument.name}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "70vh",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                    }}
                  />
                </div>
              ) : previewDocument.type === "pdf" ? (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  height: "100%"
                }}>
                  <iframe
                    src={previewDocument.url || getStaticDocumentPath('pdf')}
                    style={{
                      width: "100%",
                      height: "70vh",
                      border: "none",
                      borderRadius: "4px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                    }}
                    title={previewDocument.name}
                  />
                </div>
              ) : previewDocument.type === "excel" ? (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  height: "100%"
                }}>
                  <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewDocument.url)}`}
                    style={{
                      width: "100%",
                      height: "70vh",
                      border: "none",
                      borderRadius: "4px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                    }}
                    title={previewDocument.name}
                  />
                </div>
              ) : previewDocument.type === "word" ? (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  height: "100%"
                }}>
                  <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewDocument.url)}`}
                    style={{
                      width: "100%",
                      height: "70vh",
                      border: "none",
                      borderRadius: "4px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                    }}
                    title={previewDocument.name}
                  />
                </div>
              ) : previewDocument.type === "powerpoint" ? (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  height: "100%"
                }}>
                  <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewDocument.url)}`}
                    style={{
                      width: "100%",
                      height: "70vh",
                      border: "none",
                      borderRadius: "4px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                    }}
                    title={previewDocument.name}
                  />
                </div>
              ) : previewDocument.type === "video" ? (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "20px",
                  height: "100%",
                  backgroundColor: "#f9f9f9"
                }}>
                  <video
                    controls
                    style={{
                      width: "100%",
                      maxWidth: "800px",
                      borderRadius: "4px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                    }}
                  >
                    <source src={previewDocument.url || getStaticDocumentPath('video')} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              ) : previewDocument.type === "audio" ? (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "20px",
                  height: "100%",
                  backgroundColor: "#f9f9f9"
                }}>
                  <div style={{
                    width: "100%",
                    maxWidth: "600px",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "20px",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
                      <div style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "8px",
                        backgroundColor: "#2596be",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginRight: "15px"
                      }}>
                        <FileMusic size={30} color="#fff" />
                      </div>
                      <div>
                        <div style={{ fontSize: "16px", fontWeight: "500" }}>{previewDocument.name}</div>
                        <div style={{ fontSize: "12px", color: "#888" }}>Audio File • {previewDocument.size}</div>
                      </div>
                    </div>

                    <audio
                      controls
                      style={{ width: "100%", marginBottom: "15px" }}
                    >
                      <source src={previewDocument.url || getStaticDocumentPath('audio')} type="audio/mp3" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                </div>
              ) : previewDocument.type === "text" ? (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                  backgroundColor: "#f9f9f9",
                  height: "100%"
                }}>
                  <iframe
                    src={previewDocument.url || getStaticDocumentPath('text')}
                    style={{
                      width: "100%",
                      height: "70vh",
                      border: "none",
                      borderRadius: "4px",
                      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      backgroundColor: "white"
                    }}
                    title={previewDocument.name}
                  />
                </div>
              ) : (
                <div style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "20px",
                  height: "100%",
                  backgroundColor: "#f9f9f9"
                }}>
                  <div style={{
                    backgroundColor: "#fff",
                    padding: "30px",
                    borderRadius: "8px",
                    textAlign: "center",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                  }}>
                    <FileIcon size={60} color="#2596be" style={{ marginBottom: "15px" }} />
                    <h3 style={{ marginBottom: "10px" }}>Format not supported for preview</h3>
                    <p style={{ color: "#666", marginBottom: "20px" }}>
                      Please download the file to view its contents.
                    </p>
                    <button
                      onClick={() => handleDownload(previewDocument.url, previewDocument.name)}
                      style={{
                        padding: "8px 20px",
                        backgroundColor: "#2596be",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto"
                      }}
                    >
                      <Download size={16} style={{ marginRight: "8px" }} />
                      Download File
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Document Information Footer */}
            <div style={{
              borderTop: "1px solid #f0f0f0",
              padding: "16px 20px",
              backgroundColor: "#FAFAFA",
              display: "flex",
              justifyContent: "space-between"
            }}>
              {/* <div>
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Uploaded by</div>
          <div style={{ fontSize: "14px", fontWeight: "500" }}>{previewDocument.uploadedBy || "Unknown"}</div>
        </div> */}
              <div>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "4px" }}>Upload Date</div>
                <div style={{ fontSize: "14px", fontWeight: "500" }}>
                  {previewDocument.date}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Circulars;